import type { TFunction } from "react-i18next";
import { z } from "zod";
import { SUPPORTED_LANGUAGES } from "../../../config/i18n";
import type { SupportedLanguage } from "../../../interfaces";

// Read at a boundary, not trusted from the type: after a rollback an older API
// sends no `contentCounts`, and a new bundle must read that as "no count to
// show" rather than crash on `contentCounts[language]`.
const withContentCounts = z.object({
  contentCounts: z.record(
    z.enum(SUPPORTED_LANGUAGES),
    z.object({ articles: z.number(), collections: z.number() })
  ),
});

/**
 * What a room holds for a reader of `language`: its published articles, one
 * per paper, and its published collections. `null` when the body carries no
 * counts.
 */
export function roomContentCounts(
  room: object,
  language: SupportedLanguage
): { articles: number; collections: number } | null {
  const parsed = withContentCounts.safeParse(room);
  return parsed.success ? parsed.data.contentCounts[language] : null;
}

/**
 * The line under a room's name in a list of rooms: its partner and, when the
 * body carries them, its articles.
 */
export function roomLineSubtitle(
  room: { organisationModel: { name: string } },
  language: SupportedLanguage,
  t: TFunction
): string {
  const counts = roomContentCounts(room, language);
  return counts === null
    ? room.organisationModel.name
    : t("{{organisation}} · {{count}} articles", {
        organisation: room.organisationModel.name,
        count: counts.articles,
      });
}
